package fr.frux.shared.data.mapper

import fr.frux.shared.domain.model.Harvest
import fr.frux.shared.domain.model.HarvestParticipant
import fr.frux.shared.domain.model.HarvestStatus
import fr.frux.shared.domain.model.NearbyHarvest
import fr.frux.shared.domain.model.NearbyHarvestSiteSummary
import fr.frux.shared.domain.model.NearbyHarvestTreeSummary
import fr.frux.shared.dto.HarvestDto
import fr.frux.shared.dto.HarvestParticipantDto
import fr.frux.shared.dto.NearbyHarvestDto
import fr.frux.shared.dto.NearbyHarvestSiteSummaryDto
import fr.frux.shared.dto.NearbyHarvestTreeSummaryDto
import fr.frux.shared.dto.HarvestStatus as HarvestStatusDto

/**
 * Traduction transport ↔ domaine (#109).
 *
 * Vit dans la couche data : le domaine ne doit connaître ni les DTO ni le module
 * `backend-shared` qui les porte — sinon un changement de contrat API se propage
 * mécaniquement jusqu'aux entités métier.
 */
fun HarvestDto.toDomain(): Harvest =
    Harvest(
        id = id ?: 0,
        treeId = treeId,
        createdBy = createdBy,
        createdByUsername = createdByUsername,
        scheduledDate = scheduledDate.toEpochMilliseconds(),
        description = description,
        participants = participants,
        status = status.toDomain(),
        createdAt = createdAt?.toEpochMilliseconds() ?: 0,
        maxParticipants = maxParticipants,
        participantsDetails = participantsDetails.map { it.toDomain() },
        // Repli sur la taille des listes (#277) : un serveur antérieur au champ le laisse à 0.
        participantsCount = maxOf(participantsCount, participantsDetails.size, participants.size),
        expired = expired,
        siteId = siteId,
        siteName = siteName,
        hostedByOwner = hostedByOwner,
    )

fun HarvestParticipantDto.toDomain(): HarvestParticipant = HarvestParticipant(id = id, username = username)

fun HarvestStatusDto.toDomain(): HarvestStatus =
    when (this) {
        HarvestStatusDto.PLANNED -> HarvestStatus.PLANNED
        HarvestStatusDto.ONGOING -> HarvestStatus.ONGOING
        HarvestStatusDto.COMPLETED -> HarvestStatus.COMPLETED
        HarvestStatusDto.CANCELLED -> HarvestStatus.CANCELLED
    }

fun HarvestStatus.toDto(): HarvestStatusDto =
    when (this) {
        HarvestStatus.PLANNED -> HarvestStatusDto.PLANNED
        HarvestStatus.ONGOING -> HarvestStatusDto.ONGOING
        HarvestStatus.COMPLETED -> HarvestStatusDto.COMPLETED
        HarvestStatus.CANCELLED -> HarvestStatusDto.CANCELLED
    }

fun NearbyHarvestTreeSummaryDto.toDomain(): NearbyHarvestTreeSummary =
    NearbyHarvestTreeSummary(
        treeId = treeId,
        speciesCommonName = speciesCommonName,
        treeStatus = treeStatus.toDomain(),
    )

fun NearbyHarvestSiteSummaryDto.toDomain(): NearbyHarvestSiteSummary =
    NearbyHarvestSiteSummary(siteId = siteId, name = name, accessRule = accessRule.toDomain())

fun NearbyHarvestDto.toDomain(): NearbyHarvest =
    NearbyHarvest(
        id = id,
        treeId = treeId,
        createdBy = createdBy,
        createdByUsername = createdByUsername,
        scheduledDate = scheduledDate.toEpochMilliseconds(),
        description = description,
        participants = participants,
        status = status.toDomain(),
        createdAt = createdAt?.toEpochMilliseconds(),
        maxParticipants = maxParticipants,
        distanceMeters = distanceMeters,
        tree = tree?.toDomain(),
        site = site?.toDomain(),
        hostedByOwner = hostedByOwner,
        participantsDetails = participantsDetails.map { it.toDomain() },
        // Même repli que HarvestDto.toDomain (#277).
        participantsCount = maxOf(participantsCount, participantsDetails.size, participants.size),
    )
